import { useState, useCallback, useEffect, useRef } from "react";
import { useQuery, keepPreviousData, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { gql } from "graphql-request";
import { graphqlClient } from "@/lib/graphql";
import { ListingCard } from "@/components/listings/ListingCard";
import { SaveSearchButton } from "@/components/listings/SaveSearchButton";
import { SearchBar } from "@/components/search/SearchBar";
import { EmptyListingsBlock } from "@/components/search/EmptyListingsBlock";
import { GamePlaceholder } from "@/components/ui/GamePlaceholder";
import { RatingBadge } from "@/components/ui/RatingBadge";
import { useAuth } from "@/hooks/useAuth";
import type { Game, Listing, Wishlist } from "@/types";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useIpCity } from "@/hooks/useIpCity";
import { LANGUAGE_FLAGS } from "@/lib/languageFlags";
import {
  ImageMultiSelect,
  type ImageMultiSelectOption,
} from "@/components/ui/ImageMultiSelect";

const SEARCH_GAMES_QUERY = gql`
  query SearchGamesSuggestions($query: String!) {
    searchGames(query: $query) {
      id
      title
      imageUrl
      thumbnailUrl
      yearPublished
      playerRange
      complexityLabel
      displayRating
      ratingSource
      activeListingsCount
      lowestPrice
    }
    searchGamesCount(query: $query)
  }
`;

const WISHLISTS_QUERY = gql`
  query Wishlists {
    wishlists {
      id
      game {
        id
      }
    }
  }
`;

const TOGGLE_WISHLIST_MUTATION = gql`
  mutation ToggleWishlist($gameId: ID!) {
    toggleWishlist(gameId: $gameId) {
      wishlist {
        id
      }
      removed
    }
  }
`;

const AUTOFILL_STORAGE_KEY = "boardo:cityAutofilled";

const LISTINGS_QUERY = gql`
  query Listings(
    $query: String
    $conditions: [String!]
    $minPrice: Float
    $maxPrice: Float
    $genres: [String!]
    $languages: [String!]
    $deliveryPreference: String
    $cities: [String!]
    $isExpansion: Boolean
    $sortBy: String
    $limit: Int
    $offset: Int
  ) {
    listingsCount(
      query: $query
      conditions: $conditions
      minPrice: $minPrice
      maxPrice: $maxPrice
      genres: $genres
      languages: $languages
      deliveryPreference: $deliveryPreference
      cities: $cities
    )
    listings(
      query: $query
      conditions: $conditions
      minPrice: $minPrice
      maxPrice: $maxPrice
      genres: $genres
      languages: $languages
      deliveryPreference: $deliveryPreference
      cities: $cities
      isExpansion: $isExpansion
      sortBy: $sortBy
      limit: $limit
      offset: $offset
    ) {
      id
      price
      condition
      conditionLabel
      language
      languageLabel
      status
      createdAt
      imageUrls
      displayTitle
      displayImageUrl
      boundingBox {
        yMin
        xMin
        yMax
        xMax
      }
      siblingListings {
        id
        status
        gameTitle
        price
        boundingBox {
          yMin
          xMin
          yMax
          xMax
        }
      }
      shippingAvailable
      shippingCost
      location
      fomoBadges {
        savingsVsShops
        savingsPercent
        cheapestShopPrice
        uniqueInMarketplace
        wishlistCount
        recent
        priceDropped
        previousPrice
      }
      game {
        id
        title
        imageUrl
        thumbnailUrl
        bggRating
        displayRating
        ratingSource
      }
      seller {
        id
        displayName
        avatarUrl
        rating
        ratingsCount
      }
    }
  }
`;

const CONDITION_KEYS = ["new", "like_new", "good", "acceptable"] as const;

const CONDITION_DOT_COLORS: Record<(typeof CONDITION_KEYS)[number], string> = {
  new: "bg-emerald-500",
  like_new: "bg-green-500",
  good: "bg-slate-400",
  acceptable: "bg-amber-500",
};

const GENRES = [
  "Strategy",
  "Family",
  "Party",
  "Thematic",
  "Abstract",
  "War",
  "Economic",
  "Adventure",
  "Puzzle",
  "Trivia",
  "Card Game",
  "Dexterity",
  "Educational",
  "Horror",
  "Sci-Fi",
  "Fantasy",
  "Historical",
  "Civilization",
];

const LANGUAGE_KEYS = ["universal", "en", "lt", "ru", "de", "fr", "pl", "es", "it", "nl", "pt", "zh", "ja", "ko"] as const;

const SORT_KEYS = ["newest", "best_deals", "top_rated", "price_asc", "price_desc"] as const;

const DELIVERY_OPTIONS = ["any", "delivery", "pickup"] as const;
type DeliveryOption = (typeof DELIVERY_OPTIONS)[number];

const PICKUP_CITIES_QUERY = gql`
  query ListingPickupCities {
    listingPickupCities
  }
`;

const PAGE_SIZE = 24;

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 py-1 pl-3 pr-1 text-xs font-medium text-primary-800 ring-1 ring-inset ring-primary-100">
      <span className="max-w-[12rem] truncate">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className="ml-0.5 flex h-5 w-5 items-center justify-center rounded-full text-primary-700 hover:bg-primary-100"
        aria-label="Pašalinti filtrą"
      >
        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </span>
  );
}

function QuickChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
        active
          ? "bg-primary-800 text-white hover:bg-primary-700"
          : "border border-gray-200 bg-white text-gray-700 hover:border-primary-200 hover:bg-primary-50 hover:text-primary-800"
      }`}
    >
      {label}
    </button>
  );
}

export function ListingsPage() {
  const { t } = useTranslation();
  usePageTitle(t("listings.title"));

  const [searchParams, setSearchParams] = useSearchParams();

  const updateParams = useCallback(
    (updates: Record<string, string>, { replace = true }: { replace?: boolean } = {}) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(updates)) {
          if (!v || (v === "0" && k === "page")) next.delete(k);
          else next.set(k, v);
        }
        return next;
      }, { replace });
    },
    [setSearchParams]
  );

  const parseList = (raw: string | null): string[] =>
    raw ? raw.split(",").map((s) => s.trim()).filter(Boolean) : [];

  const search = searchParams.get("q") || "";
  const conditions = parseList(searchParams.get("conditions"));
  const minPrice = searchParams.get("minPrice") || "";
  const maxPrice = searchParams.get("maxPrice") || "";
  const genres = parseList(searchParams.get("genres"));
  const languages = parseList(searchParams.get("languages"));
  const deliveryRaw = searchParams.get("delivery") || "any";
  const delivery: DeliveryOption = (DELIVERY_OPTIONS as readonly string[]).includes(deliveryRaw)
    ? (deliveryRaw as DeliveryOption)
    : "any";
  const citiesSelected = parseList(searchParams.get("cities"));
  const isExpansionRaw = searchParams.get("isExpansion");
  const isExpansion =
    isExpansionRaw === "true" ? true : isExpansionRaw === "false" ? false : null;
  const sortBy = searchParams.get("sort") || "newest";
  const page = Number(searchParams.get("page") || "0");

  const setSearch = useCallback((v: string) => updateParams({ q: v, page: "" }), [updateParams]);
  const setConditions = useCallback(
    (v: string[]) => updateParams({ conditions: v.join(","), page: "" }),
    [updateParams]
  );
  const setMinPrice = useCallback((v: string) => updateParams({ minPrice: v, page: "" }), [updateParams]);
  const setMaxPrice = useCallback((v: string) => updateParams({ maxPrice: v, page: "" }), [updateParams]);
  const setGenres = useCallback(
    (v: string[]) => updateParams({ genres: v.join(","), page: "" }),
    [updateParams]
  );
  const setLanguages = useCallback(
    (v: string[]) => updateParams({ languages: v.join(","), page: "" }),
    [updateParams]
  );
  const setDelivery = useCallback(
    (v: DeliveryOption) =>
      updateParams({
        delivery: v === "any" ? "" : v,
        cities: v === "delivery" ? "" : citiesSelected.join(","),
        page: "",
      }),
    [updateParams, citiesSelected]
  );
  const setCities = useCallback(
    (v: string[]) => updateParams({ cities: v.join(","), page: "" }),
    [updateParams]
  );
  const addCity = useCallback(
    (v: string) => {
      if (!v || citiesSelected.includes(v)) return;
      updateParams({ cities: [...citiesSelected, v].join(","), page: "" });
    },
    [updateParams, citiesSelected]
  );
  const setSortBy = useCallback((v: string) => updateParams({ sort: v === "newest" ? "" : v, page: "" }), [updateParams]);
  const setIsExpansion = useCallback(
    (v: boolean | null) =>
      updateParams({ isExpansion: v === null ? "" : String(v), page: "" }),
    [updateParams]
  );
  const setPage = useCallback((v: number | ((p: number) => number)) => {
    const newPage = typeof v === "function" ? v(page) : v;
    updateParams({ page: newPage ? String(newPage) : "" }, { replace: false });
    window.scrollTo({ top: 0 });
  }, [updateParams, page]);

  const debouncedSearch = useDebouncedValue(search);
  const debouncedMinPrice = useDebouncedValue(minPrice);
  const debouncedMaxPrice = useDebouncedValue(maxPrice);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: [
      "listings",
      debouncedSearch,
      conditions.join(","),
      debouncedMinPrice,
      debouncedMaxPrice,
      genres.join(","),
      languages.join(","),
      delivery,
      citiesSelected.join(","),
      isExpansion,
      sortBy,
      page,
    ],
    queryFn: () =>
      graphqlClient.request<{ listings: Listing[]; listingsCount: number }>(LISTINGS_QUERY, {
        query: debouncedSearch || undefined,
        conditions: conditions.length ? conditions : undefined,
        minPrice: debouncedMinPrice ? parseFloat(debouncedMinPrice) : undefined,
        maxPrice: debouncedMaxPrice ? parseFloat(debouncedMaxPrice) : undefined,
        genres: genres.length ? genres : undefined,
        languages: languages.length ? languages : undefined,
        deliveryPreference: delivery === "any" ? undefined : delivery,
        cities: citiesSelected.length ? citiesSelected : undefined,
        isExpansion: isExpansion ?? undefined,
        sortBy,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const { data: citiesData } = useQuery({
    queryKey: ["listingPickupCities"],
    queryFn: () => graphqlClient.request<{ listingPickupCities: string[] }>(PICKUP_CITIES_QUERY),
    staleTime: 5 * 60 * 1000,
  });
  const cities = citiesData?.listingPickupCities ?? [];

  const ipCity = useIpCity();
  const autofillAttempted = useRef(false);
  useEffect(() => {
    if (autofillAttempted.current) return;
    if (citiesSelected.length > 0 || delivery === "delivery") return;
    if (!ipCity || cities.length === 0) return;
    try {
      if (localStorage.getItem(AUTOFILL_STORAGE_KEY)) return;
    } catch {
      return;
    }
    const match = cities.find((c) => c.toLowerCase() === ipCity.toLowerCase());
    if (match) {
      autofillAttempted.current = true;
      try {
        localStorage.setItem(AUTOFILL_STORAGE_KEY, "1");
      } catch {
        // ignore
      }
      addCity(match);
    }
  }, [ipCity, cities, citiesSelected, delivery, addCity]);

  const listings = data?.listings ?? [];
  const totalCount = data?.listingsCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const hasMore = page + 1 < totalPages;

  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: gamesData } = useQuery({
    queryKey: ["searchGamesSuggestions", debouncedSearch],
    queryFn: () =>
      graphqlClient.request<{
        searchGames: Game[];
        searchGamesCount: number;
      }>(SEARCH_GAMES_QUERY, {
        query: debouncedSearch,
      }),
    enabled: debouncedSearch.trim().length >= 2,
  });

  const { data: wishlistData } = useQuery({
    queryKey: ["wishlists"],
    queryFn: () =>
      graphqlClient.request<{ wishlists: Wishlist[] }>(WISHLISTS_QUERY),
    enabled: !!user,
  });

  const toggleWishlist = useMutation({
    mutationFn: (gameId: string) =>
      graphqlClient.request(TOGGLE_WISHLIST_MUTATION, { gameId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wishlists"] });
      queryClient.invalidateQueries({ queryKey: ["tracker"] });
    },
  });

  const GAME_SUGGESTIONS_LIMIT = 3;
  const allGameSuggestions = gamesData?.searchGames ?? [];
  const totalGameSuggestions = gamesData?.searchGamesCount ?? allGameSuggestions.length;
  const gameSuggestions = allGameSuggestions.slice(0, GAME_SUGGESTIONS_LIMIT);
  const remainingGameSuggestions = Math.max(
    0,
    totalGameSuggestions - gameSuggestions.length
  );
  const wishlistedIds = new Set(
    wishlistData?.wishlists.map((w) => w.game.id) ?? []
  );

  const hasFilters =
    search ||
    conditions.length > 0 ||
    minPrice ||
    maxPrice ||
    genres.length > 0 ||
    languages.length > 0 ||
    delivery !== "any" ||
    citiesSelected.length > 0 ||
    isExpansion !== null;

  const resetFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const filterSidebar = (
    <div className="space-y-6">
      {/* Price range */}
      <div>
        <h3 className="mb-3 text-sm font-semibold text-gray-900">
          {t("listings.price_eur")}
        </h3>
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={minPrice}
            onChange={(e) => {
              setMinPrice(e.target.value);
            }}
            placeholder={t("listings.price_from")}
            min="0"
            className="input w-full"
          />
          <span className="text-gray-400">-</span>
          <input
            type="number"
            value={maxPrice}
            onChange={(e) => {
              setMaxPrice(e.target.value);
            }}
            placeholder={t("listings.price_to")}
            min="0"
            className="input w-full"
          />
        </div>
      </div>

      {/* Condition */}
      <div>
        <h3 className="mb-3 text-sm font-semibold text-gray-900">{t("listings.condition")}</h3>
        <ImageMultiSelect
          options={CONDITION_KEYS.map<ImageMultiSelectOption>((key) => ({
            value: key,
            label: key === "new" ? t("conditions.new_sealed") : t(`conditions.${key}`),
            icon: (
              <span
                className={`block h-2.5 w-2.5 rounded-full ${CONDITION_DOT_COLORS[key]}`}
              />
            ),
          }))}
          values={conditions}
          onChange={setConditions}
          placeholder={t("listings.condition_select_placeholder", "Visos būklės")}
          searchPlaceholder={t("common.search", "Ieškoti...")}
          ariaLabel={t("listings.condition")}
        />
      </div>

      {/* Genre */}
      <div>
        <h3 className="mb-3 text-sm font-semibold text-gray-900">{t("listings.genre")}</h3>
        <ImageMultiSelect
          options={GENRES.map<ImageMultiSelectOption>((g) => ({
            value: g,
            label: t(`genres.${g}`, g),
          }))}
          values={genres}
          onChange={setGenres}
          placeholder={t("listings.all_genres")}
          searchPlaceholder={t("common.search", "Ieškoti...")}
          ariaLabel={t("listings.genre")}
        />
      </div>

      {/* Language */}
      <div>
        <h3 className="mb-3 text-sm font-semibold text-gray-900">{t("listings.language")}</h3>
        <ImageMultiSelect
          options={LANGUAGE_KEYS.map<ImageMultiSelectOption>((code) => ({
            value: code,
            label: t(`languages.${code}`, code),
            icon: <span>{LANGUAGE_FLAGS[code] ?? "🌐"}</span>,
          }))}
          values={languages}
          onChange={setLanguages}
          placeholder={t("listings.all_languages")}
          searchPlaceholder={t("common.search", "Ieškoti...")}
          ariaLabel={t("listings.language")}
        />
      </div>

      {/* Game type (base / expansion) */}
      <div>
        <h3 className="mb-3 text-sm font-semibold text-gray-900">Žaidimo tipas</h3>
        <select
          value={isExpansion === null ? "any" : isExpansion ? "true" : "false"}
          onChange={(e) => {
            const v = e.target.value;
            setIsExpansion(v === "any" ? null : v === "true");
          }}
          className="input w-full"
        >
          <option value="any">Visi</option>
          <option value="false">Pagrindiniai</option>
          <option value="true">Papildymai</option>
        </select>
      </div>

      {/* Delivery preference */}
      <div>
        <h3 className="mb-3 text-sm font-semibold text-gray-900">{t("listings.delivery_preference")}</h3>
        <select
          value={delivery}
          onChange={(e) => setDelivery(e.target.value as DeliveryOption)}
          className="input w-full"
        >
          {DELIVERY_OPTIONS.map((key) => (
            <option key={key} value={key}>
              {t(`listings.delivery_${key}`)}
            </option>
          ))}
        </select>
      </div>

      {/* City (pickup) */}
      {delivery !== "delivery" && cities.length > 0 && (
        <div>
          <h3 className="mb-3 text-sm font-semibold text-gray-900">{t("listings.city")}</h3>
          <ImageMultiSelect
            options={cities.map<ImageMultiSelectOption>((c) => ({
              value: c,
              label: c,
              icon: (
                <svg
                  className="h-3.5 w-3.5 text-gray-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z"
                  />
                  <circle cx="12" cy="11" r="3" strokeWidth={2} />
                </svg>
              ),
            }))}
            values={citiesSelected}
            onChange={setCities}
            placeholder={t("listings.all_cities")}
            searchPlaceholder={t("common.search", "Ieškoti...")}
            ariaLabel={t("listings.city")}
          />
        </div>
      )}

      {/* Reset */}
      {hasFilters && (
        <button
          onClick={resetFilters}
          className="w-full rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
        >
          {t("listings.clear_filters")}
        </button>
      )}
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">{t("listings.title")}</h1>
        {/* Mobile filter toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 lg:hidden"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
          {t("listings.filters")}
          {hasFilters && (
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-800 text-xs text-white">
              !
            </span>
          )}
        </button>
      </div>

      <div className="flex gap-8">
        {/* Sidebar - desktop */}
        <aside className="hidden w-64 shrink-0 lg:block">
          <div className="sticky top-24 rounded-xl border border-gray-200 p-5">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">
              {t("listings.filters")}
            </h2>
            {filterSidebar}
          </div>
        </aside>

        {/* Sidebar - mobile overlay */}
        {sidebarOpen && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/30 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <div className="fixed inset-y-0 left-0 z-50 w-80 overflow-y-auto bg-white p-6 shadow-xl lg:hidden">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  {t("listings.filters")}
                </h2>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              {filterSidebar}
            </div>
          </>
        )}

        {/* Main content */}
        <div className="min-w-0 flex-1">
          {/* Search + sort row */}
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1">
              <SearchBar
                size="lg"
                initialValue={search}
                placeholder={t("listings.search_placeholder")}
                onChange={setSearch}
                onSearch={setSearch}
              />
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <svg
                className="h-4 w-4 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h6M3 12h10M3 17h14M19 7l-3-3m0 0l-3 3m3-3v14" />
              </svg>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                aria-label={t("listings.sort_by", "Rikiuoti")}
                className="cursor-pointer rounded-lg border-0 bg-transparent py-2 pl-1 pr-8 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {SORT_KEYS.map((key) => (
                  <option key={key} value={key}>
                    {t(`sort.${key}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Quick filter chips — single row, distinct intents only */}
          <div className="mb-4 -mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0">
            <QuickChip
              label="🔥 Geriausi pasiūlymai"
              active={sortBy === "best_deals"}
              onClick={() => setSortBy(sortBy === "best_deals" ? "newest" : "best_deals")}
            />
            <QuickChip
              label="Iki 25 €"
              active={maxPrice === "25" && !minPrice}
              onClick={() => {
                if (maxPrice === "25" && !minPrice) {
                  setMaxPrice("");
                } else {
                  setMinPrice("");
                  setMaxPrice("25");
                }
              }}
            />
            <QuickChip
              label="Pagrindiniai"
              active={isExpansion === false}
              onClick={() => setIsExpansion(isExpansion === false ? null : false)}
            />
            <QuickChip
              label="Papildymai"
              active={isExpansion === true}
              onClick={() => setIsExpansion(isExpansion === true ? null : true)}
            />
          </div>
          {!isLoading && (
            <p className="mb-3 text-sm text-gray-500">
              {t("listings.result_count", {
                count: totalCount,
                defaultValue: "{{count}} skelbimų",
              })}
            </p>
          )}
          {hasFilters && (
            <div className="mb-5 flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {t("listings.active_filters", "Aktyvūs filtrai")}:
              </span>
              {search && (
                <FilterChip label={`"${search}"`} onRemove={() => setSearch("")} />
              )}
              {conditions.map((c) => (
                <FilterChip
                  key={`cond-${c}`}
                  label={c === "new" ? t("conditions.new_sealed") : t(`conditions.${c}`)}
                  onRemove={() => setConditions(conditions.filter((x) => x !== c))}
                />
              ))}
              {(minPrice || maxPrice) && (
                <FilterChip
                  label={`${minPrice || "0"}–${maxPrice || "∞"} €`}
                  onRemove={() => {
                    setMinPrice("");
                    setMaxPrice("");
                  }}
                />
              )}
              {genres.map((g) => (
                <FilterChip
                  key={`genre-${g}`}
                  label={t(`genres.${g}`, g)}
                  onRemove={() => setGenres(genres.filter((x) => x !== g))}
                />
              ))}
              {languages.map((l) => (
                <FilterChip
                  key={`lang-${l}`}
                  label={t(`languages.${l}`, l)}
                  onRemove={() => setLanguages(languages.filter((x) => x !== l))}
                />
              ))}
              {delivery !== "any" && (
                <FilterChip
                  label={t(`listings.delivery_${delivery}`)}
                  onRemove={() => setDelivery("any")}
                />
              )}
              {citiesSelected.map((c) => (
                <FilterChip
                  key={`city-${c}`}
                  label={c}
                  onRemove={() => setCities(citiesSelected.filter((x) => x !== c))}
                />
              ))}
              {isExpansion !== null && (
                <FilterChip
                  label={isExpansion ? "Papildymai" : "Pagrindiniai"}
                  onRemove={() => setIsExpansion(null)}
                />
              )}
              <button
                type="button"
                onClick={resetFilters}
                className="ml-1 text-xs font-medium text-red-600 hover:text-red-700 hover:underline"
              >
                {t("listings.clear_filters")}
              </button>
              <SaveSearchButton paramsString={searchParams.toString()} />
            </div>
          )}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-800 border-t-transparent" />
            </div>
          ) : listings.length === 0 ? (
            debouncedSearch ? (
              <EmptyListingsBlock query={debouncedSearch} />
            ) : (
            <div className="rounded-xl border border-dashed border-gray-300 py-12 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="mt-3 text-base font-semibold text-gray-700">
                {t("listings.no_results")}
              </p>
              {hasFilters ? (
                <>
                  <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">
                    Pabandyk panaikinti dalį filtrų arba pasižiūrėk panašius žaidimus žemiau.
                  </p>
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    {(minPrice || maxPrice) && (
                      <button
                        type="button"
                        onClick={() => {
                          setMinPrice("");
                          setMaxPrice("");
                        }}
                        className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Pašalinti kainos filtrą
                      </button>
                    )}
                    {citiesSelected.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setCities([])}
                        className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Pašalinti miesto filtrą
                      </button>
                    )}
                    {conditions.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setConditions([])}
                        className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Pašalinti būklės filtrą
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={resetFilters}
                      className="rounded-full bg-primary-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700"
                    >
                      Išvalyti visus filtrus
                    </button>
                  </div>
                </>
              ) : (
                <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">
                  Šiuo metu nėra aktyvių skelbimų.
                </p>
              )}
            </div>
            )
          ) : (
            <>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {listings.map((listing) => (
                  <ListingCard key={listing.id} listing={listing} />
                ))}
              </div>

              {/* Pagination */}
              <div className="mt-10 flex items-center justify-center gap-4">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="btn-secondary disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t("listings.previous")}
                </button>
                <span className="text-sm text-gray-600">
                  {t("listings.page_of", {
                    page: page + 1,
                    total: totalPages,
                    defaultValue: "Puslapis {{page}} iš {{total}}",
                  })}
                </span>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!hasMore}
                  className="btn-secondary disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t("listings.next")}
                </button>
              </div>
            </>
          )}

          {gameSuggestions.length > 0 && (
            <section className="mt-10 rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 p-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {listings.length === 0
                    ? t("listings.games_suggestions_title_empty", "Žaidimai")
                    : t("listings.games_suggestions_title", "Susiję žaidimai")}
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  {listings.length === 0
                    ? t(
                        "listings.games_suggestions_subtitle_empty",
                        "Skelbimų nerasta. Pridėkite žaidimą į norų sąrašą ir gausite pranešimą, kai atsiras naujas skelbimas."
                      )
                    : t(
                        "listings.games_suggestions_subtitle",
                        "Nerandate tinkamo skelbimo? Pridėkite žaidimą į norų sąrašą."
                      )}
                </p>
              </div>

              <div className="mt-6 space-y-3">
                {gameSuggestions.map((game) => {
                  const imageUrl = game.thumbnailUrl || game.imageUrl;
                  const isWishlisted = wishlistedIds.has(game.id);

                  return (
                    <div
                      key={game.id}
                      className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-3 shadow-sm"
                    >
                      <Link
                        to={`/games/${game.id}`}
                        className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-gray-100"
                      >
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={game.title}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <GamePlaceholder />
                        )}
                      </Link>

                      <div className="min-w-0 flex-1">
                        <Link
                          to={`/games/${game.id}`}
                          className="block truncate font-semibold text-gray-900 hover:text-primary-800"
                        >
                          {game.title}
                        </Link>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                          {game.yearPublished && <span>{game.yearPublished}</span>}
                          {game.playerRange && <span>· {game.playerRange}</span>}
                          {game.displayRating != null && (
                            <RatingBadge
                              rating={game.displayRating}
                              source={game.ratingSource ?? null}
                            />
                          )}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          {game.activeListingsCount > 0 ? (
                            <span>
                              {game.activeListingsCount}{" "}
                              {game.activeListingsCount === 1
                                ? t("listings.active_listing", "aktyvus skelbimas")
                                : t("listings.active_listings", "aktyvūs skelbimai")}
                              {game.lowestPrice != null && (
                                <span className="ml-1 font-semibold text-primary-800">
                                  {t("listings.from_price", "nuo")}{" "}
                                  {game.lowestPrice.toFixed(2)} &euro;
                                </span>
                              )}
                            </span>
                          ) : (
                            <span>
                              {t("listings.no_active_listings", "Aktyvių skelbimų nėra")}
                            </span>
                          )}
                        </div>
                      </div>

                      {user && (
                        <button
                          type="button"
                          onClick={() => toggleWishlist.mutate(game.id)}
                          disabled={toggleWishlist.isPending}
                          className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50 ${
                            isWishlisted
                              ? "bg-red-50 text-red-700 hover:bg-red-100"
                              : "bg-primary-50 text-primary-800 hover:bg-primary-100"
                          }`}
                        >
                          <svg
                            className="h-4 w-4"
                            fill={isWishlisted ? "currentColor" : "none"}
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                            />
                          </svg>
                          <span className="hidden sm:inline">
                            {isWishlisted
                              ? t("listings.wishlist_remove", "Norų sąraše")
                              : t("listings.wishlist_add", "Į norų sąrašą")}
                          </span>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {remainingGameSuggestions > 0 && (
                <div className="mt-4 text-center">
                  <Link
                    to={`/games?q=${encodeURIComponent(debouncedSearch.trim())}`}
                    onClick={() => window.scrollTo({ top: 0 })}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-800 hover:text-primary-700"
                  >
                    {t("listings.show_more_games_count", {
                      count: remainingGameSuggestions,
                    })}
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </Link>
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
